import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const OcclusalAdjustment = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Occlusal Adjustment Fort Worth TX | Bite Correction | Fort Worth Dental</title>
        <meta name="description" content="Occlusal adjustment (bite correction) in Fort Worth TX. Relieve jaw pain, TMJ issues, and uneven bite. Expert bite analysis. Call (817) 985-3337." />
        <meta name="keywords" content="occlusal adjustment fort worth, bite correction fort worth tx, TMJ treatment, jaw pain, bite alignment, malocclusion" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Occlusal (Bite) Adjustment in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Correct Your Bite for Better Oral Health</p>
            <p className="text-sm mt-4 opacity-90">Bite Correction | TMJ Treatment | Teeth Alignment</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is An Occlusal Adjustment */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/occlusal-new-1.jpg" 
                  alt="Occlusal bite adjustment Fort Worth TX - Correcting uneven bite and jaw alignment for comfort and function" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is An Occlusal Adjustment?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  An occlusal adjustment corrects the alignment of the bite. It involves carefully removing small amounts of tooth enamel to level the biting surface and improve the way your teeth come together. This dental procedure is also known as "bite adjustment" or "equilibration."
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  When upper and lower teeth come together, thousands of pounds per square inch of pressure are generated. This force needs to be evenly distributed over all of the teeth to function properly for chewing and to prevent damage. When the bite is not properly aligned, some teeth experience more force than others. This can lead to excessive tooth wear, fractures, loosening of teeth, and pain in the jaw joint (temporomandibular joint or TMJ).
                </p>
              </div>
            </div>
          </section>

          {/* What Problems Can Occlusal Misalignment Cause */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              What Problems Can Occlusal Misalignment Cause?
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              When teeth don't meet properly during biting and chewing, various problems can develop:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Excessive wear on tooth enamel, making teeth shorter and more sensitive</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Cracked or fractured teeth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Loose teeth due to excessive force</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Receding gums and bone loss</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>TMJ (temporomandibular joint) pain and dysfunction</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Headaches, particularly in the morning</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Muscle tension in the jaw, face, and neck</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Clicking or popping sounds in the jaw</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Difficulty opening and closing your mouth</span>
              </li>
            </ul>
          </section>

          {/* How Do I Know If I Have These Misalignments */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  How Do I Know If I Have These Misalignments?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Your Fort Worth dentist can determine if you have occlusal misalignment through a comprehensive examination. During the exam, we evaluate how your teeth come together and look for signs of uneven wear, damage, or stress on specific teeth.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  We may use special marking paper to identify high spots where teeth are making premature contact. You might also notice symptoms such as jaw pain, headaches, or sensitivity in certain teeth. Some patients experience clicking or popping sounds when opening their mouth, or difficulty chewing certain foods.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  If you've recently had dental work done (such as crowns, bridges, or fillings), you're at higher risk for developing bite problems if the restoration is slightly too high. Even a small discrepancy can cause discomfort and lead to larger problems over time.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/occlusal-new-2.jpg" 
                  alt="Bite Alignment Diagnosis" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* How Can This Misalignment Be Adjusted */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/occlusal-new-1.jpg" 
                  alt="Bite Adjustment Procedure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  How Can This Misalignment Be Adjusted?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  The occlusal adjustment procedure is relatively simple and painless. Using special marking paper, your dentist identifies the areas where your teeth are making premature or excessive contact. These high spots are then carefully and precisely reshaped using a dental handpiece and fine finishing burs.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Only small amounts of tooth enamel are removed – typically just a few thousandths of an inch. The goal is to create even contact across all teeth when you bite down. The procedure is conservative and designed to improve the harmony of your bite while preserving as much natural tooth structure as possible.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Most patients experience immediate relief after the adjustment. In some cases, multiple appointments may be needed to fine-tune the bite, especially if the misalignment is complex or long-standing. The procedure is usually comfortable and doesn't require anesthesia, though some patients prefer local anesthesia if they have sensitive teeth.
                </p>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Benefits of Occlusal Adjustment</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Relieves jaw pain and TMJ symptoms</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Eliminates headaches caused by bite problems</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Prevents excessive and uneven tooth wear</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Improves chewing efficiency</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Reduces stress on jaw joints and muscles</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Protects existing dental work and restorations</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Prevents future tooth fractures</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Occlusal Adjustment?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team uses precise techniques to ensure your bite is properly balanced.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Precision Technology</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Occlusal Adjustment Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about bite adjustment in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Occlusal Adjustment Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default OcclusalAdjustment;